import React from 'react';
import './BodyHitmap.css';

/**
 * Body silhouette hitmap showing hit distribution percentages.
 * Only highlights the most-hit zone; rest stays dark/muted.
 * @param {Object} props
 * @param {Object} props.hitgroups - Object with head, chest, stomach, arm, leg counts.
 * @param {string} [props.weaponFilter] - Currently selected weapon name for display.
 */
export default function BodyHitmap({ hitgroups, weaponFilter }) {
  if (!hitgroups) return null;

  const total = hitgroups.head + hitgroups.chest + hitgroups.stomach + hitgroups.arm + hitgroups.leg;
  if (total === 0) return null;

  const pct = (val) => Math.round((val / total) * 100);
  const zones = [
    { key: 'head', label: 'Head', value: hitgroups.head, pct: pct(hitgroups.head), color: '#ff6b00' },
    { key: 'chest', label: 'Chest', value: hitgroups.chest, pct: pct(hitgroups.chest), color: '#22c55e' },
    { key: 'stomach', label: 'Stomach', value: hitgroups.stomach, pct: pct(hitgroups.stomach), color: '#8b5cf6' },
    { key: 'arm', label: 'Arms', value: hitgroups.arm, pct: pct(hitgroups.arm), color: '#6b7280' },
    { key: 'leg', label: 'Legs', value: hitgroups.leg, pct: pct(hitgroups.leg), color: '#3b82f6' },
  ];

  const maxKey = zones.reduce((a, b) => (b.value > a.value ? b : a)).key;

  const zoneColor = (key) => (key === maxKey ? 'active' : '');

  return (
    <div className="body-hitmap">
      <div className="body-hitmap-figure">
        <svg viewBox="0 0 100 220" className="body-silhouette">
          {/* Head */}
          <circle cx="50" cy="24" r="16" className={`body-zone zone-head ${zoneColor('head')}`} />
          {/* Neck */}
          <rect x="45" y="39" width="10" height="8" rx="2" className={`body-zone zone-head ${zoneColor('head')}`} />
          {/* Chest */}
          <rect x="28" y="47" width="44" height="40" rx="4" className={`body-zone zone-chest ${zoneColor('chest')}`} />
          {/* Stomach */}
          <rect x="30" y="87" width="40" height="28" rx="3" className={`body-zone zone-stomach ${zoneColor('stomach')}`} />
          {/* Left Arm */}
          <rect x="12" y="49" width="14" height="52" rx="7" className={`body-zone zone-arm ${zoneColor('arm')}`} />
          {/* Right Arm */}
          <rect x="74" y="49" width="14" height="52" rx="7" className={`body-zone zone-arm ${zoneColor('arm')}`} />
          {/* Left Leg */}
          <rect x="32" y="117" width="16" height="60" rx="6" className={`body-zone zone-leg ${zoneColor('leg')}`} />
          {/* Right Leg */}
          <rect x="52" y="117" width="16" height="60" rx="6" className={`body-zone zone-leg ${zoneColor('leg')}`} />
          {/* Left Foot */}
          <rect x="30" y="175" width="18" height="8" rx="4" className={`body-zone zone-leg ${zoneColor('leg')}`} />
          {/* Right Foot */}
          <rect x="52" y="175" width="18" height="8" rx="4" className={`body-zone zone-leg ${zoneColor('leg')}`} />
        </svg>
      </div>
      <div className="body-hitmap-stats">
        {weaponFilter && (
          <div className="hitmap-filter-label">{weaponFilter}</div>
        )}
        {zones.map(z => (
          <div key={z.key} className={`hitmap-row ${z.key === maxKey ? 'hitmap-row-highlight' : ''}`}>
            <span className="hitmap-label">{z.label}</span>
            <span className="hitmap-pct" style={{ color: z.key === maxKey ? z.color : undefined }}>{z.pct}%</span>
            <span className="hitmap-hits">{z.value} Hits</span>
          </div>
        ))}
      </div>
    </div>
  );
}
